const getHost = () => {
  const requestEvent = useRequestEvent();
  if (requestEvent) {
    return requestEvent.node.req.headers.host;
  }

  return window.location.host;
};

const isTokenExpired = (token) => {
  try {
    const payload = token.split('.')[1];

    let decodedPayload;
    if (typeof Buffer !== 'undefined') {
      decodedPayload = Buffer.from(payload, 'base64').toString();
    } else {
      decodedPayload = window.atob(payload);
    }

    const { exp } = JSON.parse(decodedPayload);

    return Date.now() >= exp * 1000;
  } catch (err) {
    console.error('Invalid token:', err);
    return true;
  }
};

// The latch below only clears once the refresh settles, so an unbounded one
// would park every later request on a dead promise. Deliberately above the
// API's worst-case rate-limit delay of `(max - delayAfter) * timeWait` = 40s
// (see api/services/_rate-limit.js): the server rotates the token before it
// answers, so aborting a slow-but-live refresh spends the token without ever
// receiving its replacement — turning a survivable wait into a logout.
const REFRESH_TIMEOUT_MS = 45 * 1000;

// Refresh tokens are single-use server-side: concurrent refreshes race and all
// but the first are rejected into a logout, so they share one in-flight call.
// Module state is safe here only because the app is `ssr: false`.
let refreshInFlight = null;

// The latch is per tab but the cookies are shared, so another tab can win the
// rotation and leave ours rejected. It already wrote the replacement, so adopt
// that rather than reading a lost race as a dead session and logging out.
const adoptRotatedToken = () => {
  const rotated = useCookie('token').value;

  return rotated && !isTokenExpired(rotated) ? rotated : null;
};

const refreshSession = (baseURL, refresh) => {
  if (!refreshInFlight) {
    refreshInFlight = $fetch('/public/users/refresh', {
      baseURL,
      method: 'POST',
      // A signal rather than ofetch's own `timeout`, which stops counting once
      // the headers arrive and so leaves a stalled body unbounded — the one
      // case that would strand the latch.
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
      body: {
        refresh,
      },
    })
      .then((response) => {
        const authStore = useAuthStore();
        authStore.setAuth(response);

        return authStore.token;
      })
      .catch(adoptRotatedToken)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
};

const request = (method) => {
  return async(url, options) => {
    let token = useCookie('token').value;
    const refresh = useCookie('refresh').value;
    const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';
    const baseURL = `${protocol}://${getHost().replace(useRuntimeConfig().public.port, useRuntimeConfig().public.apiPort)}/api`;

    if (token && refresh && isTokenExpired(token)) {
      token = (await refreshSession(baseURL, refresh)) || token;
    }

    const requestOptions = {
      ...options,

      // All after the spread, so a caller can neither drop Authorization by
      // passing its own `headers` nor redirect an authenticated request — a
      // caller-supplied `baseURL` would otherwise send the bearer token to
      // whatever host it named.
      method,
      baseURL,
      headers: {
        ...(options?.headers || {}),
        ...(token ? { Authorization: token } : {}),
      },

      onResponseError({ response }) {
        // logout() itself is authenticated and can 401 — its own isLoggingOut
        // guard absorbs the re-entrant call, so no special-casing is needed here.
        if (response?.status === 401) {
          useAuthStore().logout();
        }
      },
    };

    return $fetch(url, requestOptions);
  };
};

export default {
  delete: request('DELETE'),
  get: request('GET'),
  patch: request('PATCH'),
  post: request('POST'),
  put: request('PUT'),
};
